export const normalizeRole = (role) => String(role || '')
  .trim()
  .toLowerCase()
  .replace(/[\s_-]/g, '');

export const getCurrentUserRole = (user) => {
  const candidates = [
    user?.role?.name, user?.role?.slug, user?.role,
    user?.data?.role?.name, user?.data?.role?.slug, user?.data?.role,
    user?.user?.role?.name, user?.user?.role?.slug, user?.user?.role,
    user?.roles?.[0]?.name, user?.roles?.[0]?.slug, user?.roles?.[0],
    user?.data?.roles?.[0]?.name, user?.data?.roles?.[0]?.slug, user?.data?.roles?.[0],
    user?.user?.roles?.[0]?.name, user?.user?.roles?.[0]?.slug, user?.user?.roles?.[0],
  ];
  return candidates.find((value) => typeof value === 'string' && value.trim()) || '';
};

export const isSuperAdminUser = (user) =>
  ['superadmin', 'superadministrator'].includes(normalizeRole(getCurrentUserRole(user)));

const validId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value);
  return /^[1-9]\d*$/.test(normalized) ? normalized : null;
};

const storedId = (key) => {
  try {
    return typeof window !== 'undefined' ? validId(window.localStorage.getItem(key)) : null;
  } catch {
    return null;
  }
};

/**
 * A super admin has no implied establishment. They must select one or use
 * the establishment on the order being edited. Stored choices are hints only.
 * Do not interpret the user's role as authorization for a mismatched menu.
 */
export const getUserEstablishmentId = (user) => {
  if (isSuperAdminUser(user)) return null;
  const records = [user, user?.data, user?.user];
  for (const record of records) {
    if (!record) continue;
    const candidates = [
      record.establishment_id, record.establishment?.id,
      record.managed_establishment_id, record.managed_establishment?.id,
      record.managedEstablishment?.id,
      record.managed_establishments?.[0]?.id,
      record.managedEstablishments?.[0]?.id,
      record.restaurant_id, record.restaurant?.id,
      record.business?.establishment_id, record.business?.establishment?.id,
    ];
    for (const candidate of candidates) {
      const id = validId(candidate);
      if (id) return id;
    }
  }
  // For managers whose API has not loaded establishment relationships yet,
  // a manually selected establishment may be used as a UI hint. Backend
  // authorization still decides whether the manager can access it.
  return storedId('selected_establishment_id');
};

/** Use when switching establishments: never preserve a menu from another one. */
export const getMenuForEstablishment = (menus, menuId, establishmentId) => {
  const establishment = validId(establishmentId);
  const selected = validId(menuId);
  if (!establishment || !selected || !Array.isArray(menus)) return null;
  return menus.find((menu) =>
    validId(menu?.id) === selected && validId(menu?.establishment_id) === establishment
  ) || null;
};

/** Order editing: its saved IDs are authoritative, not localStorage. */
export const getQrOrderDesignContext = (order, orderItem) => ({
  establishmentId: validId(order?.establishment_id),
  menuId: validId(orderItem?.design?.menu_id),
  orderItemId: validId(orderItem?.id),
});

export const getPaginationItems = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const getPaginationMeta = (response) => {
  const payload = response?.data?.data;
  if (payload && !Array.isArray(payload)) {
    return {
      currentPage: payload.current_page ?? payload.meta?.current_page ?? 1,
      lastPage: payload.last_page ?? payload.meta?.last_page ?? 1,
      total: payload.total ?? payload.meta?.total ?? 0,
    };
  }
  return { currentPage: 1, lastPage: 1, total: Array.isArray(payload) ? payload.length : 0 };
};

export const formatMoney = (value, currency = 'MAD') => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(2)} ${currency}` : '—';
};

export const getMediaUrl = (path) => {
  if (!path) return '';
  const raw = String(path).trim();
  if (/^(https?:)?\/\//i.test(raw) || /^(blob:|data:)/i.test(raw)) return raw;
  // Create React App environment. Keep index.php for hosts whose storage
  // assets are served by the root front controller.
  const configuredBase = typeof process !== 'undefined'
    ? (process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_API_URL || '')
    : '';
  const base = String(configuredBase)
    .replace(/\/api\/?$/i, '')
    .replace(/\/index\.php\/?$/i, '')
    .replace(/\/$/, '');
  const cleanPath = raw.replace(/^\/+/, '');
  if (/^(index\.php\/)?storage\//i.test(cleanPath)) {
    const normalizedPath = cleanPath.replace(/^index\.php\//i, '');
    return `${base}/index.php/${normalizedPath}`;
  }
  return `${base}/index.php/storage/${cleanPath}`;
};

export const getOptionValue = (option) =>
  String(typeof option === 'string' || typeof option === 'number'
    ? option : option?.value ?? option?.id ?? option?.label ?? '');

export const getOptionLabel = (option) =>
  String(typeof option === 'string' || typeof option === 'number'
    ? option : option?.label ?? option?.name ?? option?.value ?? option?.id ?? '');

export const getOrderProgress = (status) => {
  const steps = ['design_confirmed', 'in_production', 'ready', 'shipped', 'completed'];
  const awaitingDesign = ['quotation_requested', 'negotiating', 'price_accepted'].includes(status);
  const index = awaitingDesign ? 0 : Math.max(0, steps.indexOf(status));
  return { steps, index };
};
